import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DollarSign, ShoppingBag, Users, Clock, AlertTriangle, Truck, ClipboardCheck, ListChecks, GraduationCap, Star, QrCode } from "lucide-react";
import { ActionTile, fmtDT } from "@/components/admin/ui";
import { statusLabel } from "@/lib/ordering";

function CountReminder({ status }) {
  if (!status || status.store_counted_today) return null;
  if (status.cutoff_passed) return (
    <div className="mb-5 p-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 text-sm flex items-center gap-3" data-testid="count-reminder-late">
      <ClipboardCheck className="w-5 h-5 shrink-0"/><span><b>Inventory count missing today.</b> All employees are required to submit their inventory count before 12:00 PM — please count now (it will be marked late).</span>
    </div>
  );
  if (status.minutes_to_cutoff > 60) return null;
  return (
    <div className="mb-5 p-4 rounded-2xl bg-orange-50 border border-orange-200 text-orange-900 text-sm flex items-center gap-3" data-testid="count-reminder-soon">
      <Clock className="w-5 h-5 shrink-0"/><span><b>Count inventory soon.</b> Today's count is due in {status.minutes_to_cutoff} minutes (12:00 PM).</span>
    </div>
  );
}

function QuickActions({ sid, quick, staff }) {
  const nav = useNavigate();
  const tiles = [
    { icon: ClipboardCheck, label: "Count Inventory", hint: quick?.last_count ? `Last: ${fmtDT(quick.last_count.submitted_at)}` : "No count yet today", to: "inventory", tone: "bg-neutral-950 text-white", testId: "qa-count" },
    { icon: Truck, label: "Request Restock", hint: `${quick?.restock_open ?? 0} open requests`, to: "restock", tone: "bg-[hsl(var(--primary))] text-white", testId: "qa-restock" },
    { icon: ListChecks, label: "My Tasks", hint: `${quick?.my_tasks_open ?? 0} open`, to: "tasks", tone: "bg-blue-600 text-white", testId: "qa-tasks" },
    { icon: GraduationCap, label: "Training", hint: `${quick?.training_percent ?? 0}% complete`, to: "training", tone: "bg-emerald-600 text-white", testId: "qa-training" },
  ].filter((t) => staff || ["inventory", "restock"].includes(t.to));
  if (!staff) tiles.push({ icon: Users, label: "Employees", hint: "Tasks, training, documents & time clock", to: "employees", tone: "bg-blue-600 text-white", testId: "qa-employees" });
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="quick-actions">
      {tiles.map((t) => <ActionTile key={t.to} {...t} onClick={() => nav(`/admin/stores/${sid}/${t.to}`)}/>)}
    </div>
  );
}

export default function StoreOverview() {
  const { sid } = useParams();
  const { user } = useAuth();
  const isAdminLike = ["super_admin", "admin", "manager"].includes(user?.role);
  const [data, setData] = useState(null);
  const [quick, setQuick] = useState(null);
  const [countStatus, setCountStatus] = useState(null);

  useEffect(() => {
    api.get(`/stores/${sid}/quick`).then((r) => setQuick(r.data)).catch(() => {});
    api.get(`/inventory/count-status?store_id=${sid}`).then((r) => setCountStatus(r.data)).catch(() => {});
    if (isAdminLike) api.get(`/stores/${sid}/overview`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [sid, isAdminLike]);

  if (!isAdminLike) {
    return (
      <div className="p-6 lg:p-10">
        <div className="mb-6">
          <p className="eyebrow mb-2">Welcome back</p>
          <h1 className="font-display font-bold text-3xl tracking-tight" data-testid="store-overview-title">Hi, {user?.name?.split(" ")[0]}</h1>
          <p className="text-sm text-neutral-500 mt-1">What would you like to do?</p>
        </div>
        <CountReminder status={countStatus}/>
        <QuickActions sid={sid} quick={quick} staff/>
      </div>
    );
  }

  if (data === null) return <div className="p-8 text-neutral-500">Loading overview…</div>;
  if (data === false) return <div className="p-8 text-neutral-500">Could not load this store.</div>;

  const t = data.today;
  const cards = [
    { icon: DollarSign, label: "Today's Sales", value: `$${t.sales.toFixed(2)}`, testid: "kpi-sales" },
    { icon: ShoppingBag, label: "Orders Today", value: t.orders, testid: "kpi-orders" },
    { icon: Users, label: "Clocked In", value: t.clocked_in, testid: "kpi-clocked" },
    { icon: Clock, label: "Labor Hours", value: t.labor_hours.toFixed(1), testid: "kpi-labor" },
    { icon: AlertTriangle, label: "Low Stock Items", value: t.low_stock_count, testid: "kpi-low", warn: t.low_stock_count > 0 },
    { icon: Truck, label: "Restock Requests", value: quick?.restock_new ?? 0, testid: "kpi-restock", warn: (quick?.restock_new ?? 0) > 0, hint: "new", to: "restock" },
    { icon: ListChecks, label: "Open Tasks", value: quick?.tasks_open ?? 0, testid: "kpi-tasks", to: "employees" },
    { icon: Star, label: "Feedback", value: quick?.feedback_avg != null ? `${quick.feedback_avg} ★` : "—", testid: "kpi-feedback", hint: `${quick?.feedback_count ?? 0} reviews`, to: "feedback" },
  ];

  return (
    <div className="p-6 lg:p-10">
      <div className="mb-6">
        <p className="eyebrow mb-2">Store · Today</p>
        <h1 className="font-display font-bold text-3xl tracking-tight" data-testid="store-overview-title">{data.store.name}</h1>
        <p className="text-sm text-neutral-500 mt-1">{data.store.address}</p>
      </div>

      <CountReminder status={countStatus}/>
      <QuickActions sid={sid} quick={quick} staff={false}/>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(({ icon: Icon, label, value, testid, warn, hint, to }) => (
          <Link key={testid} to={to ? `/admin/stores/${sid}/${to}` : "#"} onClick={(e) => !to && e.preventDefault()} className={`block bg-white rounded-2xl border border-neutral-200/70 p-5 ${to ? "hover:shadow-md hover:-translate-y-0.5 transition-all" : "cursor-default"}`} data-testid={testid}>
            <div className={`w-9 h-9 rounded-xl inline-flex items-center justify-center mb-3 ${warn ? "bg-orange-50 text-orange-600" : "bg-neutral-100 text-neutral-700"}`}>
              <Icon className="w-4 h-4"/>
            </div>
            <div className="text-xs uppercase tracking-wider text-neutral-500">{label}</div>
            <div className="mt-1 font-display font-bold text-2xl">{value} {hint && <span className="text-xs font-normal text-neutral-400">{hint}</span>}</div>
          </Link>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap gap-2 text-xs">
        <Link to={`/admin/stores/${sid}/qr`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-neutral-200 bg-white hover:bg-neutral-50" data-testid="link-qr"><QrCode className="w-3.5 h-3.5"/>QR Codes</Link>
        <Link to={`/admin/stores/${sid}/feedback`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-neutral-200 bg-white hover:bg-neutral-50" data-testid="link-feedback"><Star className="w-3.5 h-3.5"/>Feedback</Link>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mt-6">
        <div className="bg-white rounded-2xl border border-neutral-200/70 p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-semibold">Recent Orders</h2>
            <Link to={`/admin/stores/${sid}/orders`} className="text-xs text-[hsl(var(--primary))] font-medium">View all →</Link>
          </div>
          {data.recent_orders.length === 0
            ? <p className="text-sm text-neutral-500" data-testid="recent-orders-empty">No orders yet.</p>
            : <ul className="divide-y divide-neutral-100">
                {data.recent_orders.map((o) => (
                  <li key={o.id} className="py-2.5 flex items-center justify-between text-sm">
                    <div>
                      <div className="font-medium">{o.customer_name || "Walk-in"} · #{(o.id || "").slice(0, 6)}</div>
                      <div className="text-[11px] text-neutral-500">{(o.created_at || "").slice(0, 16).replace("T", " ")} · {statusLabel(o.status)}</div>
                    </div>
                    <div className="font-semibold">${(o.total || 0).toFixed(2)}</div>
                  </li>
                ))}
              </ul>}
        </div>

        <div className="bg-white rounded-2xl border border-neutral-200/70 p-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display font-semibold">Recent Restock Requests</h2>
            <Link to={`/admin/stores/${sid}/restock`} className="text-xs text-[hsl(var(--primary))] font-medium">View all →</Link>
          </div>
          {data.recent_restock.length === 0
            ? <p className="text-sm text-neutral-500" data-testid="recent-restock-empty">No restock requests.</p>
            : <ul className="divide-y divide-neutral-100">
                {data.recent_restock.map((r) => (
                  <li key={r.id} className="py-2.5 flex items-center justify-between text-sm">
                    <div>
                      <div className="font-medium">{r.name} · {r.quantity} {r.unit}</div>
                      <div className="text-[11px] text-neutral-500">by {r.requested_by_name} · {r.priority}</div>
                    </div>
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-700 capitalize">{r.status.replace("_", " ")}</span>
                  </li>
                ))}
              </ul>}
        </div>
      </div>

      {data.low_stock.length > 0 && (
        <div className="mt-6 bg-white rounded-2xl border border-neutral-200/70 p-5">
          <h2 className="font-display font-semibold mb-3">Low-stock alerts</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.low_stock.map((i) => (
              <div key={i.id} className="flex items-center justify-between p-3 rounded-lg bg-orange-50 border border-orange-100">
                <div>
                  <div className="font-medium text-sm">{i.name}</div>
                  <div className="text-[11px] text-neutral-500">{i.quantity} / {i.reorder_level} {i.unit}</div>
                </div>
                <AlertTriangle className="w-4 h-4 text-orange-600"/>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
